import React, { createContext, useContext, useEffect, useState } from 'react';
import Joyride, { Step, CallBackProps, STATUS, ACTIONS, TooltipRenderProps, Placement } from 'react-joyride';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Button } from '@/components/ui/button';
import { Rocket, ChartBar, BookText, Wallet, Bot, Star, ArrowRight } from 'lucide-react';

interface TourContextType {
  startTour: (pageId: string) => void;
  resetTour: (pageId: string) => void;
  isActive: boolean;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within TourProvider');
  }
  return context;
};

const getStepIcon = (index: number) => {
  switch (index) {
    case 0:
      return <ChartBar className="w-5 h-5 text-tradebrand-green" />;
    case 1:
      return <Rocket className="w-5 h-5 text-tradebrand-green" />;
    case 2:
      return <Star className="w-5 h-5 text-tradebrand-green" />;
    case 3:
      return <Wallet className="w-5 h-5 text-tradebrand-green" />;
    case 4:
      return <ArrowRight className="w-5 h-5 text-tradebrand-green" />;
    case 5:
      return <Star className="w-5 h-5 text-tradebrand-green" />;
    case 6:
      return <Bot className="w-5 h-5 text-tradebrand-green" />;
    default:
      return null;
  }
};

interface TourProviderProps {
  children: React.ReactNode;
}

// Utility: Checks if a selector finds any element on the page
function isTargetAvailable(selector: string) {
  if (!selector) return false;
  try {
    // Remove wrapping for attribute selectors
    return document.querySelector(selector) !== null;
  } catch {
    return false;
  }
}

// NEW: fallback selector for prop-trading-bot-detail steps
const FALLBACK_INFO_CARD_SELECTOR = '[data-tour="prop-bot-info"]';

// Helper: get a valid target for tour step (fallback if not found)
function getValidTarget(target: string | undefined, fallback?: string) {
  if (target && isTargetAvailable(target)) {
    return target;
  }
  if (fallback && isTargetAvailable(fallback)) {
    return fallback;
  }
  return target || fallback || ''; // return the original if nothing else works (for Joyride)
}

export const TourProvider = ({ children }: TourProviderProps) => {
  const [isActive, setIsActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState<Step[]>([]);
  const location = useLocation();
  const { user } = useAuth();
  
  const getTourSteps = (pageId: string): Step[] => {
    const baseSteps = {
      dashboard: [
        {
          target: '[data-tour="stats-cards"]',
          title: 'Your Bot & Account Stats',
          content: 'These cards show key statistics — the number of trading accounts you\'ve connected, the number of My Bots you\'ve created, and how many Prop Bots and Premium Bots you\'ve integrated.',
          placement: 'bottom' as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="ai-prop"]',
          title: 'Get Funded with Up to $200,000',
          content: 'This is a real funding program offering up to $200,000 for users who trade using automated bots supported by the AI Funder prop firm.',
          placement: 'bottom' as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="bot-performance"]',
          title: 'Most Used Bots',
          content: 'Explore the most popular Premium Bots and Prop Trading Bots — trusted by many traders for their consistent performance.',
          placement: 'left' as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="menu-accounts"]',
          title: 'Connect Your Trading Accounts',
          content: 'Connect your trading accounts from supported platforms and exchanges.',
          placement: 'right' as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="menu-prop-trading"]',
          title: 'Pass Prop Challenges Automatically',
          content: 'These bots are designed to help you pass prop firm challenges and receive real funding from AI Funder. Integration is fast and easy.',
          placement: 'right' as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="menu-premium"]',
          title: 'Trade with Premium Bots',
          content: 'Premium Bots allow you to trade automatically and generate real profits on your live accounts.',
          placement: 'right' as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="menu-my-bots"]',
          title: 'Your Custom Trading Bots',
          content: 'These are the bots you\'ve connected manually through TradingView — fully customizable to your own strategy.',
          placement: 'right' as Placement,
          disableBeacon: true,
        }
      ],
      'my-bots': [
  {
    target: '[data-tour="bots-header"]',
    title: 'Your Bot Management Hub',
    content: (
      <div>
        This is where you manage all your personal trading bots (User Bots).  
        You can view, edit, or pause any bot you've created.
      </div>
    ),
    placement: 'bottom' as Placement,
  },
  {
    target: '[data-tour="deploy-bot"]',
    title: 'Create a New Bot',
    content: (
      <div>
        Click here to deploy a new trading bot.  
        You can define a custom name, connect your strategy via TradingView, and configure its settings.
      </div>
    ),
    placement: 'bottom' as Placement,
  },
  {
    target: '[data-tour="bot-filters"]',
    title: 'Search & Filter Bots',
    content: (
      <div>
        Use the search bar and filters to quickly find your bots.  
        You can filter by name, ID, or status (Active/Inactive).
      </div>
    ),
    placement: 'bottom' as Placement,
  },
  {
    target: '[data-tour="bot-card"]',
    title: 'Bot Overview Card',
    content: (
      <div>
        Each card shows key bot information — name, description, status, connected accounts, and supported platforms like cTrader, MT4, or MT5.
      </div>
    ),
    placement: 'top' as Placement,
  },
  {
    target: '[data-tour="bot-card-actions"]',
    title: 'Bot Controls',
    content: (
      <div>
        - Click <strong>View Details</strong> to see bot configurations<br />
        - Click <strong>Pause</strong> to stop bot execution temporarily<br />
        These actions help you manage bot behavior in real time.
      </div>
    ),
    placement: 'top' as Placement,
  },
],
      'exchange-accounts': [
        {
          target: '[data-tour="trading-platforms"]',
          title: 'Trading Platforms',
          content: 'Choose platforms that support trading FX, Gold, and CFDs. At this stage we support the cTrader platform. MetaTrader 4/5 will be enabled in the next phase.',
          placement: 'bottom' as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="crypto-exchanges"]',
          title: 'Cryptocurrency Exchanges',
          content: 'Select the crypto exchanges you want to connect. We plan to add connections to all major exchanges in the near future.',
          placement: 'bottom' as Placement,
          disableBeacon: true,
        },
        {
          target: '.platform-card-ctrader [data-tour="create-profile"]',
          title: 'Create cTrader Profile',
          content: 'Click "+ Create Profile" to create a cTrader profile. This profile can be used for prop‑firm challenges and any broker that supports the cTrader platform.',
          placement: 'right' as Placement,
          disableBeacon: true,
        }
      ],
      
      'manage-ctrader': [
        {
          target: '[data-tour="profiles-grid"]',
          title: 'cTrader Profiles',
          content: 'These cards show the cTrader profiles you\'ve created. Each profile can contain multiple trading accounts that you can connect.',
          placement: 'bottom' as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="card-actions"]',
          title: 'Profile Actions',
          content: 'Use these actions to manage your profile:\n\n• Disconnect: All trading accounts inside this profile will be disconnected.\n• Reconnect: All trading accounts will automatically reconnect.\n• Delete: Remove the profile (requires removing all connected accounts first).',
          placement: 'left' as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="search-create"]',
          title: 'Search and Create Profiles',
          content: 'Search your existing profiles by email or name. The Create Profile button shows how many profiles you can create based on your subscription.',
          placement: 'bottom' as Placement,
          disableBeacon: true,
        }
      ],
      
      'ctrader-profile-detail': [
        {
          target: '[data-tour="accounts-section"]',
          title: 'Button "Connect Trading Account n/n"',
          content: (
  <div className="text-sm leading-relaxed space-y-2">
    <p><strong>N / N INDICATOR</strong><br />
    Your current number of connected trading accounts versus the maximum allowed by your subscription plan.</p>

    <p><strong>API CREDENTIALS</strong><br />
    API Name: Enter a friendly name for your API so you can easily manage it.<br />
    Client ID: Copy this from your cTrader Open API dashboard.<br />
    Secret Key: Copy this from your cTrader Open API dashboard.<br />
    Access Token: Generate this in the Sandbox section using your Client ID and Secret Key.</p>

    <p><strong>TEST CONNECTION</strong><br />
    After filling in all credentials, click Test Connection to load your trading accounts list.</p>

    <p><strong>CONNECT</strong><br />
    Once the test succeeds, select the trading account you wish to connect and click Connect.</p>
  </div>
),
          placement: 'bottom' as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="search-accounts"]',
          title: 'Search Trading Accounts',
          content: 'Use this search bar to filter and find specific trading accounts by their name or details.',
          placement: 'bottom' as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="connect-dialog"]',
          title: 'Connect New Trading Account',
          content: 'Click here to connect a new trading account to your profile.',
          placement: 'left' as Placement,
          disableBeacon: true,
        }
      ],
      "premium-bots": [
        {
          target: '[data-tour="premium-bots-header"]',
          title: "Discover Premium Bots",
          content:
            "This is the Premium Bot library of Trade Bot 365 — featuring various trading strategies across markets like Crypto, Forex, Gold, and CFD. Choose the bot that matches your trading style.",
          placement: "bottom" as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="premium-stats-cards"]',
          title: "Choose by Performance and Risk",
          content:
            "Each bot displays key metrics like P. Profit (expected profit) and Max DD (maximum historical drawdown). Higher profit usually comes with higher risk — so pick according to your risk appetite.",
          placement: "bottom" as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="premium-bot-action"]',
          title: "“Use this bot” vs “Manage” Button",
          content:
            "If you haven’t connected a trading account to the bot, the button shows “Use this bot”. If already connected, it changes to “Manage”. Both options take you to the bot's detailed page.",
          placement: "bottom" as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="premium-search-filter"]',
          title: "Search & Filter Bots",
          content:
            "Use the search bar and filters to narrow down bots by risk level or choose standout bots like “Best Seller”, “Featured”, or “New”.",
          placement: "bottom" as Placement,
          disableBeacon: true,
        },
      ],
      "premium-bot-detail": [
        {
          target: '[data-tour="detail-description"]',
          title: "Bot Description & Supported Pairs",
          content:
            "Carefully read the bot's description to understand its trading style and strategy. The “Support Pairs” section shows which symbols the bot will trade — essential for aligning with your portfolio.",
          placement: "bottom" as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="detail-stats"]',
          title: "Key Statistics",
          content:
            "Important performance metrics such as Potential Profit (monthly), Max Drawdown, and Winrate help you estimate expected performance and risk before using the bot.",
          placement: "bottom" as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="detail-features"]',
          title: "Bot Features",
          content:
            "Explore the key features the bot offers — including its risk management style, order handling, and signal strategy. This helps you understand how the bot operates under the hood.",
          placement: "bottom" as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="detail-info"]',
          title: "Technical Information",
          content: (
            <div className="text-sm leading-relaxed space-y-2">
              <p>
                Pay close attention to the <strong>Min Capital</strong> — the recommended minimum amount for safe and optimal bot performance.
              </p>
              <p>
                <span className="font-mono px-2 py-1 rounded bg-accent text-foreground">Example:</span>
                If Min Capital is <b>$1,000</b> and your account has only <b>$500</b>, it may carry more risk.<br />
                On the other hand, if you have <b>$10,000</b>, set <i>Volume Multiplier</i> to <b>x10</b> to maintain accurate trade sizing.
              </p>
            </div>
          ),
          placement: "bottom" as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="detail-use-bot"]',
          title: "“Use This Bot” or “+ Add New Account” Button",
          content: (
            <div className="text-sm leading-relaxed space-y-1">
              <p>You’ll be asked to select:</p>
              <ul className="list-disc pl-5">
                <li><strong>CTrader Profile</strong>: previously created in your Exchange Accounts section.</li>
                <li><strong>Trading Account</strong>: the account you wish to connect to the bot.</li>
              </ul>
              <p>
                <span className="text-red-600 font-bold">Note:</span> Each trading account can only be assigned to one bot at a time.
              </p>
            </div>
          ),
          placement: "bottom" as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="detail-multiplier"]',
          title: "Select an Appropriate Volume Multiplier",
          content: (
            <div className="text-sm leading-relaxed space-y-1">
              <p>This value adjusts the trade volume the bot will execute.</p>
              <p>
                <span className="font-mono px-2 py-1 rounded bg-accent text-foreground">Example:</span>
                If Min Capital is <b>$1,000</b> and your account has <b>$10,000</b> → select <b>x10</b> to keep proper trade sizing.
              </p>
            </div>
          ),
          placement: "bottom" as Placement,
          disableBeacon: true,
        },
        {
          target: '[data-tour="detail-connected-accounts"]',
          title: "Connected Accounts & Signal History",
          content:
            "Once a trading account is connected, you’ll be able to monitor it under the Connected Accounts and Signal History sections for real-time tracking and performance insights.",
          placement: "bottom" as Placement,
          disableBeacon: true,
        },
      ],
      "prop-trading": [
        {
          target: '[data-tour="prop-trading-header"]',
          title: "Introduction to the Prop Trading Bots Page",
          content:
            "🗂 This is where you’ll find Prop Bots built specifically for passing funded challenges on Prop Firm — making it easier to qualify for trading capital up to $200,000 with confidence and automation.",
          placement: "bottom",
          disableBeacon: true,
        },
        {
          target: '[data-tour="prop-bot-card"]',
          title: "How to choose the right bot",
          content: (
            <div className="text-sm space-y-2">
              <p>📊 Metrics like <b>Pass Rate</b> (success rate of users passing the challenge) and <b>Est. Pass Time</b> (estimated time to complete the challenge) help you evaluate which bot fits your trading style and goals.</p>
              <p>🔁 Usually, the higher the pass rate, the longer the estimated time — it’s all about balancing speed and consistency.</p>
            </div>
          ),
          placement: "bottom",
          disableBeacon: true,
        },
        {
          target: '[data-tour="start-challenge-btn"]',
          title: "“Start Challenge with Bot” or “Manage” Button",
          content: (
            <div className="text-sm space-y-2">
              <p>💡 If you haven’t connected the bot to a trading account yet, you’ll see the “Start Challenge with Bot” button.</p>
              <p>If already connected, it changes to <b>Manage</b>.<br />👉 Both buttons lead to the bot’s detailed page where you can configure and track your challenge progress.</p>
            </div>
          ),
          placement: "bottom",
          disableBeacon: true,
        },
        {
          target: '[data-tour="prop-bot-search"]',
          title: "Bot search & filter",
          content: (
            <div className="text-sm space-y-2">
              <p>🔍 Use the search bar and filters to explore bots by risk level or discover popular ones under categories like Best Seller, Featured, or New — making it easier to find the strategy that fits your needs.</p>
            </div>
          ),
          placement: "bottom",
          disableBeacon: true,
        }
      ],
      "prop-trading-bot-detail": [
        {
          target: '[data-tour="prop-bot-info"]',
          title: "Bot Description & Supported Pairs",
          content:
            "This section provides a detailed description of the bot’s strategy and the minimum capital required.\n\n👉 Make sure to check the Supported Pairs to see which trading symbols the bot operates on — and whether they fit your challenge setup.",
          placement: "bottom",
          disableBeacon: true,
        },
        {
          target: '[data-tour="prop-bot-stats"]',
          title: "Performance Statistics",
          content:
            "Review key metrics such as Pass Rate, Est. Pass Time, Win Rate, Max Drawdown, and Potential Profit to understand the bot’s performance profile.\n\n📈 Higher pass rate may suggest more consistency, but usually requires a longer challenge duration.",
          placement: "bottom",
          disableBeacon: true,
        },
        {
          target: '[data-tour="prop-bot-features"]',
          title: "Bot Features",
          content:
            "These are the unique features that help the bot optimize performance for Prop Firm challenges — including trade logic, capital management, and signal handling methods.",
          placement: "bottom",
          disableBeacon: true,
        },
        {
          target: '[data-tour="prop-bot-requirements"]',
          title: "Usage Requirements",
          content:
            "These are the minimum conditions for using this Prop Trading Bot. Be sure the bot’s strategy aligns with your challenge rules and risk structure.",
          placement: "bottom",
          disableBeacon: true,
        },
        {
          target: '[data-tour="prop-bot-rules"]',
          title: "Challenge Rules",
          content:
            "Review the rules enforced by your selected Prop Firm — such as max drawdown, number of trading days, or position size limits — and verify the bot complies accordingly.",
          placement: "bottom",
          disableBeacon: true,
        },
        {
          target: '[data-tour="add-account-btn"]',
          title: "Connect Your Trading Account",
          content: (
            <div>
              <p>You’ll need to select:</p>
              <ul className="list-disc pl-5 text-sm">
                <li><b>CTrader Profile:</b> Already created in your Exchange Accounts</li>
                <li><b>Trading Account:</b> The account to connect to this bot</li>
              </ul>
              <p className="mt-2"><b>Note:</b> Each trading account can only be assigned to one bot at a time.</p>
            </div>
          ),
          placement: "bottom",
          disableBeacon: true,
        },
        {
          target: '[data-tour="prop-bot-volume-multiplier"]',
          title: "Choose the Right Volume Multiplier",
          content: (
            <div className="text-sm space-y-2">
              <p>The multiplier determines trade size independently of account balance.</p>
              <p>
                <b>Example:</b> <br />
                If Min Capital = $10,000 and your account is $10,000 → use <b>x1</b><br />
                If your account is $100,000 → use <b>x10</b> to maintain volume ratio
              </p>
            </div>
          ),
          placement: "bottom",
          disableBeacon: true,
        },
        {
          target: '[data-tour="prop-bot-accounts-section"]',
          title: "Connected Accounts & Signal History",
          content: (
            <div className="text-sm space-y-2">
              <p>Once a trading account is linked, you’ll be able to monitor:</p>
              <ul className="list-disc pl-5">
                <li><b>Connected Accounts</b> – currently active accounts using this bot</li>
                <li><b>Signal History</b> – logs of each signal and how it was processed</li>
              </ul>
              <p>🎯 This helps you stay informed and track bot activity in real time.</p>
            </div>
          ),
          placement: "bottom",
          disableBeacon: true,
        },
      ],
    }[pageId] || [];

    // For prop-trading-bot-detail only, set fallback
    const isPropBotDetail = pageId === "prop-trading-bot-detail";
    const fallbackTarget = isPropBotDetail ? FALLBACK_INFO_CARD_SELECTOR : undefined;

    // At step creation, override missing targets
    return baseSteps.map((step, index) => {
      let resolvedTarget = typeof window !== "undefined"
        ? getValidTarget(step.target, fallbackTarget)
        : (step.target || fallbackTarget || '');

      return {
        ...step,
        target: resolvedTarget,
        disableBeacon: true,
        tooltipComponent: (props: TooltipRenderProps) => {
          const { continuous, index, step, backProps, closeProps, primaryProps, skipProps, tooltipProps } = props;
          return (
            <div
              {...tooltipProps}
              className="relative p-4 rounded-lg bg-card border border-border shadow-xl max-w-md"
              style={{
                filter: 'drop-shadow(0 20px 13px rgba(0, 0, 0, 0.03)) drop-shadow(0 8px 5px rgba(0, 0, 0, 0.08))'
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                {getStepIcon(index)}
                <h3 className="text-lg font-semibold">{step.title}</h3>
              </div>
              <div className="mb-4 text-muted-foreground">{step.content}</div>
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <button {...backProps} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Back
                  </button>
                  <button {...skipProps} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Skip
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {index + 1} of {baseSteps.length}
                  </span>
                  <Button {...primaryProps} variant="default" className="bg-tradebrand-green hover:bg-tradebrand-green/90">
                    {index === baseSteps.length - 1 ? 'Finish' : 'Next'}
                  </Button>
                </div>
              </div>
            </div>
          );
        },
      };
    });
  };

  const startTour = (pageId: string) => {
    const tourSteps = getTourSteps(pageId);
    setSteps(tourSteps);
    setStepIndex(0);
    setIsActive(true);
  };

  const resetTour = (pageId: string) => {
    const tourSteps = getTourSteps(pageId);
    setSteps(tourSteps);
    setStepIndex(0);
    setIsActive(true);
  };

  const handleTourCallback = (data: CallBackProps) => {
    const { action, index, status, type } = data;
    
    // Fix for the type error - use direct comparison instead of includes
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setIsActive(false);
    } else if (type === 'step:after') {
      // Handle next/prev navigation
      if (action === ACTIONS.NEXT && index < steps.length - 1) {
        setStepIndex(index + 1);
      } else if (action === ACTIONS.PREV && index > 0) {
        setStepIndex(index - 1);
      } else if (action === ACTIONS.NEXT && index === steps.length - 1) {
        setIsActive(false); // Only close on last step
      }
    }
  };

  useEffect(() => {
    setIsActive(false);
  }, [location.pathname]);

  return (
    <TourContext.Provider value={{ startTour, resetTour, isActive }}>
      <Joyride
        steps={steps}
        run={isActive}
        stepIndex={stepIndex}
        continuous
        showSkipButton
        showProgress
        disableCloseOnEsc={false}
        disableOverlayClose={false}
        spotlightClicks
        scrollToFirstStep
        scrollOffset={120}
        hideCloseButton
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: '#0ac389',
            textColor: 'hsl(var(--foreground))',
            backgroundColor: 'hsl(var(--card))',
            overlayColor: 'rgba(0, 0, 0, 0.5)',
            spotlightShadow: '0 0 15px rgba(10, 195, 137, 0.3)',
            beaconSize: 36,
          },
          spotlight: {
            borderRadius: 'var(--radius)',
          },
          tooltipContainer: {
            textAlign: 'left',
          },
          buttonNext: {
            backgroundColor: '#0ac389',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '6px',
          },
          buttonBack: {
            marginRight: 10,
            color: 'hsl(var(--muted-foreground))',
          },
          buttonSkip: {
            color: 'hsl(var(--muted-foreground))',
          },
        }}
        floaterProps={{
          hideArrow: true,
        }}
        callback={handleTourCallback}
      />
      {children}
    </TourContext.Provider>
  );
};
